import { useEffect, useRef, useState } from 'react';
import { useTheme } from '@mui/material';
import { fontFamily, grey, radius, semantic } from '../theme/tokens';

/**
 * Loads the Square Web Payments SDK from Square's CDN (sandbox or production
 * per the pay-page view), attaches a card field to `#square-card-container`,
 * and exposes tokenize(). The SDK script is injected once and cached on
 * window; the card instance is destroyed on unmount.
 */

// Minimal typings for the slice of the SDK we use — Square ships no types.
interface SquareCard {
  attach(selector: string): Promise<void>;
  tokenize(): Promise<{
    status: string;
    token?: string;
    errors?: { message?: string }[];
  }>;
  destroy(): Promise<void>;
}

interface SquarePayments {
  card(options?: Record<string, unknown>): Promise<SquareCard>;
}

declare global {
  interface Window {
    Square?: {
      payments(applicationId: string, locationId: string): SquarePayments;
    };
  }
}

const SCRIPT_URLS = {
  sandbox: 'https://sandbox.web.squarecdn.com/v1/square.js',
  production: 'https://web.squarecdn.com/v1/square.js',
};

function loadSquareScript(environment: 'sandbox' | 'production'): Promise<void> {
  return new Promise((resolve, reject) => {
    if (window.Square) {
      resolve();
      return;
    }
    const src = SCRIPT_URLS[environment];
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve());
      existing.addEventListener('error', () => reject(new Error('Square SDK failed to load')));
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Square SDK failed to load'));
    document.head.appendChild(script);
  });
}

interface UseSquareWebPaymentsOptions {
  applicationId?: string;
  locationId?: string;
  environment?: 'sandbox' | 'production';
  /** The card iframe only mounts while this is true. */
  enabled: boolean;
}

export function useSquareWebPayments({
  applicationId,
  locationId,
  environment = 'sandbox',
  enabled,
}: UseSquareWebPaymentsOptions) {
  const theme = useTheme();
  const cardRef = useRef<SquareCard | null>(null);
  const [ready, setReady] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const dark = theme.palette.mode === 'dark';

  useEffect(() => {
    if (!enabled || !applicationId || !locationId) return;

    let cancelled = false;

    (async () => {
      try {
        await loadSquareScript(environment);
        if (cancelled || !window.Square) return;

        const payments = window.Square.payments(applicationId, locationId);
        // Styled from the design tokens so the iframe doesn't read as foreign.
        const card = await payments.card({
          style: {
            input: {
              fontSize: '16px',
              fontFamily: fontFamily,
              color: dark ? grey[100] : grey[900],
            },
            'input.error': { color: semantic[dark ? 'dark' : 'light'].danger.main },
            '.input-container': {
              borderColor: dark ? grey[700] : grey[300],
              borderRadius: `${radius.control}px`,
            },
            '.input-container.is-focus': {
              borderColor: semantic[dark ? 'dark' : 'light'].info.main,
            },
          },
        });
        if (cancelled) {
          card.destroy();
          return;
        }
        await card.attach('#square-card-container');
        if (cancelled) {
          card.destroy();
          return;
        }
        cardRef.current = card;
        setReady(true);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Square SDK failed to load');
        }
      }
    })();

    return () => {
      cancelled = true;
      setReady(false);
      cardRef.current?.destroy();
      cardRef.current = null;
    };
     
  }, [enabled, applicationId, locationId, environment, dark]);

  /** Resolves to a card token (sourceId), or throws with a displayable message. */
  const tokenize = async (): Promise<string> => {
    if (!cardRef.current) {
      throw new Error('The card form is not ready yet');
    }
    const result = await cardRef.current.tokenize();
    if (result.status !== 'OK' || !result.token) {
      const message = result.errors?.map((e) => e.message).filter(Boolean).join('; ');
      throw new Error(message || 'The card details could not be verified');
    }
    return result.token;
  };

  return { ready, loadError, tokenize };
}
